import React, { useMemo, useState } from "react";
import {
  Box,
  Card,
  Checkbox,
  Collapse,
  FormControlLabel,
  IconButton,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import _ from "lodash";
import moment from "moment";
import { useSelector } from "react-redux";
import { TopicChip as Chip } from '../../common/topicChip';
import { locationsSelector } from "../../../../store/slices/location";
import { normalizeReasoningEffort } from "../../../../common/utils";
import { isValidJSON } from "../reviewComponents/reviewUtils";
import { PromptAssignmentChips, getCompanyChipLabel, getPromptAssignment, useAssignmentsByPrompt } from "./promptAssignmentChips";

const formatDate = (value) => value ? moment.utc(value).local().format("MMM D, YYYY h:mm A") : "";

// Label/value pairs in two aligned columns.
const PropertyList = ({ items }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1.5, rowGap: 0.5, alignContent: 'start' }}>
    {_.map(items, ({ label, value }) => (
      <React.Fragment key={label}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="body2">{value || '—'}</Typography>
      </React.Fragment>
    ))}
  </Box>
);

// Clickable group header: chevron, title and how many prompts the group holds.
const GroupHeader = ({ expanded, onToggle, count, sx, children }) => (
  <Stack
    direction={'row'}
    alignItems={'center'}
    gap={0.5}
    role="button"
    tabIndex={0}
    aria-expanded={expanded}
    onClick={onToggle}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onToggle();
      }
    }}
    sx={{ ...sx, cursor: 'pointer', userSelect: 'none' }}
  >
    {expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
    {children}
    <Typography variant="body2" color="text.secondary">({count})</Typography>
  </Stack>
);

// Full-page version of the Manage Prompts list: one full-width card per prompt, grouped and sorted the same
// way (type, sub type, name). Quick search matches every value shown on a card, including the chips.
const PromptReport = ({ data, onOpenPrompt }) => {
  const theme = useTheme();
  const [searchText, setSearchText] = useState("");
  const [excludeArchived, setExcludeArchived] = useState(true);
  // Groups follow the Expand All / Collapse All default unless their header was clicked since.
  // Keys are the type, or "type/subType" for a sub type.
  const [defaultExpanded, setDefaultExpanded] = useState(true);
  const [toggledGroups, setToggledGroups] = useState({});
  const { users } = useSelector(locationsSelector);
  const assignmentsByPrompt = useAssignmentsByPrompt();

  const userNames = useMemo(() => _.mapValues(_.keyBy(users, 'userId'),
    (user) => user.firstName + (user.lastName ? " " + user.lastName : "")), [users]);

  const rows = useMemo(() => _.map(data, (prompt) => {
    const params = (isValidJSON(prompt?.defaultParameters) && JSON.parse(prompt.defaultParameters)) || {};
    const assignment = getPromptAssignment(assignmentsByPrompt, prompt);
    const row = {
      prompt,
      assignment,
      isArchived: prompt?.status === 3,
      reasoningEffort: params.reasoning_effort ? normalizeReasoningEffort(prompt?.llm, params.reasoning_effort) : "",
      createdBy: userNames[prompt?.createdBy] ?? "",
      createdOn: formatDate(prompt?.createdOn),
      modifiedBy: userNames[prompt?.modifiedBy] ?? "",
      modifiedOn: formatDate(prompt?.modifiedOn),
    };
    row.searchText = _.toLower(_.join(_.compact([
      prompt?.name, prompt?.version && `v${prompt.version}`, prompt?.type, prompt?.subType, prompt?.description,
      prompt?.llm, row.reasoningEffort, row.isArchived && "Archived",
      row.createdBy, row.createdOn, row.modifiedBy, row.modifiedOn, prompt?.promptId,
      ..._.map(assignment?.envs, 'label'),
      ..._.flatMap(assignment?.companyEnvs, (env) => [getCompanyChipLabel(env), ...env.companies]),
      !(assignment?.envs?.length || assignment?.companyEnvs?.length) && "Not assigned in any environment",
    ]), " "));
    return row;
  }), [data, assignmentsByPrompt, userNames]);

  const filteredRows = useMemo(() => {
    const terms = _.words(_.toLower(searchText), /[^\s]+/g);
    return _.filter(rows, (row) =>
      !(excludeArchived && row.isArchived) && _.every(terms, (term) => _.includes(row.searchText, term)));
  }, [rows, searchText, excludeArchived]);

  // type -> subType -> rows, in the same order as the Manage Prompts list.
  const groupedRows = useMemo(() => {
    const sorted = _.sortBy(_.orderBy(filteredRows, ['prompt.name']), ['prompt.type', 'prompt.subType', 'prompt.name']);
    const result = {};
    _.forEach(sorted, (row) => {
      const type = row.prompt?.type;
      const subTypeKey = row.prompt?.subType || 'No SubType';
      if (!result[type]) result[type] = {};
      if (!result[type][subTypeKey]) result[type][subTypeKey] = [];
      result[type][subTypeKey].push(row);
    });
    return result;
  }, [filteredRows]);

  const isExpanded = (key) => toggledGroups[key] ?? defaultExpanded;
  const toggleGroup = (key) => setToggledGroups((prev) => ({ ...prev, [key]: !(prev[key] ?? defaultExpanded) }));
  const setAllExpanded = (expanded) => {
    setDefaultExpanded(expanded);
    setToggledGroups({});
  };

  const groupHeaderSx = {
    px: 2,
    py: 1,
    backgroundColor: theme.palette.mode === 'dark' ? "rgba(255, 255, 255, 0.075)" : "rgba(0, 0, 0, 0.05)",
  };
  const subTypeHeaderSx = {
    px: 2,
    py: 0.75,
    my: 1,
    background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : "#F8F9FC",
  };
  // The middle and right areas get a divider on wide screens, where the three areas sit side by side.
  const areaSx = { borderLeft: { md: `1px solid ${theme.palette.divider}` }, pl: { md: 2 } };

  return (
    <Box sx={{ height: 'calc(100vh - 200px)', display: 'flex', flexDirection: 'column', pr: 3 }}>
      <Stack direction={'row'} alignItems={'center'} gap={2} sx={{ py: 1.5 }}>
        <TextField
          size="small"
          label="Quick search"
          placeholder="Name, description, LLM, environment, company, author..."
          value={searchText}
          onChange={(event) => {
            setSearchText(event.target.value);
            // Show every match: typing expands all groups.
            setAllExpanded(true);
          }}
          sx={{ width: 480 }}
        />
        <FormControlLabel
          control={<Checkbox checked={excludeArchived} onChange={(event) => setExcludeArchived(event.target.checked)} />}
          label="Exclude Archived"
        />
        <Stack direction={'row'} alignItems={'center'} gap={1}>
          <Link component="button" variant="body2" onClick={() => setAllExpanded(true)}>Expand All</Link>
          <Typography variant="body2" color="text.secondary">|</Typography>
          <Link component="button" variant="body2" onClick={() => setAllExpanded(false)}>Collapse All</Link>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          {filteredRows.length} of {rows.length} prompts
        </Typography>
      </Stack>
      <Box sx={{ overflowY: 'auto', flexGrow: 1, pb: 2 }}>
        {_.isEmpty(groupedRows) &&
          <Typography color="text.secondary" sx={{ p: 2 }}>No prompts match your search.</Typography>
        }
        {_.map(_.keys(groupedRows), (type) => (
          <Box key={type} sx={{ mb: 2 }}>
            <GroupHeader
              expanded={isExpanded(type)}
              onToggle={() => toggleGroup(type)}
              count={_.sumBy(_.values(groupedRows[type]), 'length')}
              sx={groupHeaderSx}
            >
              <Typography fontSize={'larger'}><strong>{type}</strong></Typography>
            </GroupHeader>
            <Collapse in={isExpanded(type)} unmountOnExit>
            {_.map(_.keys(groupedRows[type]), (subType) => {
              const subTypeKey = type + '/' + subType;
              const hasHeader = subType !== 'No SubType' || type === 'Review Request';
              return (
              <Box key={subType}>
                {hasHeader &&
                  <GroupHeader
                    expanded={isExpanded(subTypeKey)}
                    onToggle={() => toggleGroup(subTypeKey)}
                    count={groupedRows[type][subType].length}
                    sx={subTypeHeaderSx}
                  >
                    <Typography variant="subtitle2">{subType === 'No SubType' ? "ALL" : subType}</Typography>
                  </GroupHeader>
                }
                <Collapse in={!hasHeader || isExpanded(subTypeKey)} unmountOnExit>
                <Stack gap={1} sx={{ px: 1, mt: subType === 'No SubType' ? 1 : 0 }}>
                  {_.map(groupedRows[type][subType], (row) => (
                    <Card
                      key={row.prompt.promptId}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: '8px',
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '5fr 2fr 3fr' },
                        gap: 2,
                        opacity: row.isArchived ? 0.7 : 1,
                      }}
                    >
                      <Stack gap={0.75}>
                        <Stack direction={'row'} alignItems={'center'} gap={0.5}>
                          <Typography fontWeight={600}>{row.prompt.name}</Typography>
                          {row.isArchived &&
                            <Chip label="Archived" sx={{ ml: 0.5, height: '20px', fontSize: '11px' }} />
                          }
                          <Tooltip title="Open in Manage Prompts" arrow disableInteractive>
                            <IconButton size="small" onClick={() => onOpenPrompt(row.prompt)}>
                              <OpenInNewIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                        {row.prompt.description &&
                          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                            {row.prompt.description}
                          </Typography>
                        }
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 'auto' }}>
                          Created{row.createdBy ? ` by ${row.createdBy}` : ''} on {row.createdOn}
                          {' · '}Modified{row.modifiedBy ? ` by ${row.modifiedBy}` : ''} on {row.modifiedOn}
                          <br />Prompt Id: {row.prompt.promptId}
                        </Typography>
                      </Stack>
                      <Box sx={areaSx}>
                        <PropertyList items={[
                          { label: 'Version', value: row.prompt.version },
                          { label: 'LLM', value: row.prompt.llm },
                          { label: 'Reasoning Effort', value: _.capitalize(row.reasoningEffort) },
                        ]} />
                      </Box>
                      <Box sx={areaSx}>
                        {(row.assignment?.envs?.length > 0 || row.assignment?.companyEnvs?.length > 0)
                          ? <PromptAssignmentChips assignment={row.assignment} isArchived={row.isArchived} />
                          : <Typography variant="body2" color="text.secondary">Not assigned in any environment</Typography>
                        }
                      </Box>
                    </Card>
                  ))}
                </Stack>
                </Collapse>
              </Box>
              );
            })}
            </Collapse>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default PromptReport;
